import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  UpdateAPInvoiceLineItemInput,
  UpdateAPInvoiceLineItemResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook for updating AP invoice line item fields (quantity, unitPrice).
 * Handles the API call, success/error states, and cache invalidation.
 */
export function useUpdateAPInvoiceLineItem() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    UpdateAPInvoiceLineItemResponse,
    Error,
    UpdateAPInvoiceLineItemInput
  >({
    mutationFn: async (input) => {
      return await rpcClient.accountsPayable.invoices.updateLineItem(input);
    },
    onSuccess: (data, variables) => {
      showToast({
        title: 'Success',
        message: 'Line item updated successfully',
        color: 'success',
        icon: 'check',
      });

      // Invalidate invoice queries to refresh the data
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsPayable.invoices.get.queryKey({
          input: { id: variables.apInvoiceId },
        }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update line item',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
